// src/services/faqService.js - 完整版本（含背景處理工作狀態查詢 + 批次圖片 URL）
import { apiRequest } from './apiService';

const faqService = {
  /**
   * 取得所有 FAQ（FaqPage 使用 — 保留 displayNumber）
   */
  getAllFaqs: async (isTest = false, companyId = null) => {
    try {
      let endpoint;
      if (isTest) {
        endpoint = `faq/test/getAllByCompany?companyId=${companyId || '2'}`;
      } else {
        endpoint = companyId ? `faq?companyId=${companyId}` : `faq`;
      }

      const response = await apiRequest('get', endpoint, null, isTest);

      return {
        data: response.data.items || []
      };
    } catch (error) {
      throw new Error(error.response?.data?.message || '取得FAQ列表時發生錯誤');
    }
  },

  /**
   * 搜尋 FAQ
   */
  searchFaq: async (query, isTest = false) => {
    try {
      const endpoint = isTest
        ? `faq/test/search?query=${encodeURIComponent(query)}`
        : `faq/search?query=${encodeURIComponent(query)}`;

      const response = await apiRequest('get', endpoint, null, isTest);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '搜尋FAQ時發生錯誤');
    }
  },

  /**
   * 取得上傳歷史
   */
  getFileUploads: async (isTest = false) => {
    try {
      const endpoint = isTest ? 'faq/test/uploads' : 'faq/uploads';
      const response = await apiRequest('get', endpoint, null, isTest);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '取得上傳歷史時發生錯誤');
    }
  },

  /**
   * 上傳 FAQ Excel 檔案
   */
  uploadFaqExcel: async (file, isTest = false) => {
    try {
      const endpoint = isTest ? 'faq/test/upload' : 'faq/upload';

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiRequest('post', endpoint, formData, isTest);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '上傳FAQ檔案時發生錯誤');
    }
  },

  /**
   * 查詢背景處理工作狀態（輪詢備援用）
   * 對應後端路由: GET /api/faq/upload-jobs/:jobId
   * @param {string} jobId - 工作 ID（UUID）
   * @param {boolean} isTest - 是否為測試模式
   * @returns {Promise<Object>} 工作狀態資訊
   */
  getUploadJobStatus: async (jobId, isTest = false) => {
    try {
      const endpoint = isTest
        ? `faq/test/upload-jobs/${jobId}`
        : `faq/upload-jobs/${jobId}`;

      const response = await apiRequest('get', endpoint, null, isTest);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '查詢工作狀態時發生錯誤');
    }
  },

  /**
   * 產生圖片上傳預簽名 URL
   */
  generatePresignedUrls: async (files, type, entityId, isTest = false, conversationId = null, tagAnswerId = null) => {
    try {
      const endpoint = 'images/pre-signed-urls';

      const payload = {
        files: files.map(file => ({
          filename: file.name,
          mimeType: file.type || 'image/jpeg',
          size: file.size || 0
        })),
        type: type
      };

      if (entityId !== null && entityId !== undefined) {
        const parsedId = parseInt(entityId);

        if (!isNaN(parsedId) && parsedId > 0 && parsedId <= 2147483647) {
          payload.entityId = parsedId;
        }
      }

      if (type === 'message' && conversationId) {
        const parsedConversationId = parseInt(conversationId);
        if (!isNaN(parsedConversationId) && parsedConversationId > 0) {
          payload.conversationId = parsedConversationId;
        }
      }

      // 標籤回答圖片：傳入 tagAnswerId
      if (tagAnswerId) {
        const parsedTagAnswerId = parseInt(tagAnswerId);
        if (!isNaN(parsedTagAnswerId) && parsedTagAnswerId > 0) {
          payload.tagAnswerId = parsedTagAnswerId;
        }
      }

      const response = await apiRequest('post', endpoint, payload, isTest);
      return response.data;
    } catch (error) {
      const errorData = error.response?.data;
      const errorCode = errorData?.errorCode;
      const errorMessage = errorData?.message;

      if (errorCode === 'FAQ_NOT_FOUND') {
        throw new Error('指定的 FAQ 不存在。請先保存 FAQ 後再上傳圖片。');
      } else if (errorCode === 'CONVERSATION_NOT_FOUND') {
        throw new Error('指定的對話不存在。');
      } else if (errorCode === 'FOREIGN_KEY_CONSTRAINT_ERROR') {
        throw new Error('關聯的資料不存在，請檢查相關記錄。');
      } else if (errorMessage?.includes('不存在或不屬於您的公司')) {
        throw new Error('指定的 FAQ 不存在或您沒有權限訪問。');
      }

      throw new Error(errorMessage || '生成上傳 URL 時發生錯誤');
    }
  },

  /**
   * 上傳圖片到 S3
   */
  uploadToS3: async (presignedUrl, file) => {
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });

      if (!response.ok) {
        throw new Error(`S3 上傳失敗: ${response.status} ${response.statusText}`);
      }

      return { success: true };
    } catch (error) {
      throw new Error('上傳圖片到 S3 時發生錯誤: ' + error.message);
    }
  },

  /**
   * 確認檔案上傳
   */
  confirmUpload: async (token, fileSize, isTest = false) => {
    try {
      const endpoint = 'images/confirm-upload';

      const payload = {
        token: token,
        fileSize: fileSize
      };

      const response = await apiRequest('post', endpoint, payload, isTest);
      return response;
    } catch (error) {
      throw new Error(error.response?.data?.message || '確認上傳時發生錯誤');
    }
  },

  /**
   * 取得單張圖片代理 URL
   */
  getImageProxyUrl: async (imageId, isTest = false) => {
    try {
      const endpoint = `images/proxy/${imageId}`;

      const response = await apiRequest('get', endpoint, null, isTest);

      if (response.data && response.data.url) {
        const url = response.data.url;

        if (url.includes('/api/images/serve/')) {
          response.data.url = url.replace('/api/images/serve/', '/api/images/public/serve/');
        }
      }

      return response.data;
    } catch (error) {
      return {
        url: `/api/images/proxy/${imageId}`,
        error: true
      };
    }
  },

  /**
   * 批次取得圖片代理 URL（陣列格式）
   * 回傳格式: [{ id, url, expiresAt }]
   */
  getBatchImageProxyUrls: async (imageIds, isTest = false) => {
    try {
      if (!imageIds || imageIds.length === 0) {
        return [];
      }

      const endpoint = 'images/batch-urls';
      const payload = {
        imageIds: imageIds,
        imageType: 'faq'
      };

      const response = await apiRequest('post', endpoint, payload, isTest);

      if (response.data && response.data.urls) {
        const validUrls = response.data.urls.filter(item => item.url !== null);

        const result = validUrls.map(item => ({
          id: item.id,
          url: item.url,
          expiresAt: item.expiresAt
        }));

        const foundIds = new Set(validUrls.map(item => item.id));
        const missingIds = imageIds.filter(id => !foundIds.has(id));

        missingIds.forEach(id => {
          result.push({
            id: id,
            url: `/api/images/proxy/${id}`,
            error: true
          });
        });

        return result;
      }

      return imageIds.map(id => ({
        id: id,
        url: `/api/images/proxy/${id}`
      }));

    } catch (error) {
      return imageIds.map(id => ({
        id: id,
        url: `/api/images/proxy/${id}`
      }));
    }
  },

  /**
   * 批次取得圖片代理 URL（Map 格式 — FaqPage.fetchFaqs 使用）
   * 回傳格式: { urls: { [imageId]: proxyUrl } }
   * @param {Array<number>} imageIds - 圖片 ID 陣列
   * @param {string} imageType - 圖片類型 ('faq')
   * @param {boolean} isTest - 是否為測試模式
   * @returns {Promise<Object>} { urls: { [imageId]: proxyUrl } }
   */
  getBatchProxyUrls: async (imageIds, imageType = 'faq', isTest = false) => {
    try {
      if (!imageIds || imageIds.length === 0) {
        return { urls: {} };
      }

      const endpoint = 'images/batch-urls';
      const payload = {
        imageIds: imageIds,
        imageType: imageType
      };

      const response = await apiRequest('post', endpoint, payload, isTest);

      // 後端回傳 { urls: [{ id, url, expiresAt }, ...] }
      // 轉換為 { urls: { [id]: url } } 供 FaqPage 使用
      if (response.data && response.data.urls && Array.isArray(response.data.urls)) {
        const urlsMap = {};
        response.data.urls.forEach(item => {
          if (item.id && item.url) {
            urlsMap[item.id] = item.url;
          }
        });
        return { urls: urlsMap };
      }

      return { urls: {} };
    } catch (error) {
      return { urls: {} };
    }
  },

  /**
   * 上傳 FAQ 圖片
   */
  uploadFaqImages: async (images, faqId, isTest = false, tagAnswerId = null) => {
    try {
      const imagesToUpload = images.filter(img => img.file);

      if (imagesToUpload.length === 0) {
        return { uploadedImages: [] };
      }

      let validatedFaqId = null;
      if (faqId !== null && faqId !== undefined) {
        const parsedId = parseInt(faqId);
        if (!isNaN(parsedId) && parsedId > 0 && parsedId <= 2147483647) {
          validatedFaqId = parsedId;
        }
      }

      let presignedResponse;
      try {
        presignedResponse = await faqService.generatePresignedUrls(
          imagesToUpload.map(img => img.file),
          'faq',
          validatedFaqId,
          isTest,
          null,
          tagAnswerId
        );
      } catch (urlError) {
        if (urlError.message.includes('FAQ 不存在') && validatedFaqId) {
          presignedResponse = await faqService.generatePresignedUrls(
            imagesToUpload.map(img => img.file),
            'faq',
            null,
            isTest,
            null,
            tagAnswerId
          );
        } else {
          throw urlError;
        }
      }

      const presignedData = presignedResponse.data || presignedResponse;
      const uploadUrls = presignedData.images || presignedData.uploadUrls || [];

      if (uploadUrls.length === 0) {
        throw new Error('未能獲取上傳 URLs');
      }

      const uploadPromises = [];

      for (let i = 0; i < imagesToUpload.length; i++) {
        const image = imagesToUpload[i];
        const presignedInfo = uploadUrls[i];

        if (!presignedInfo || !presignedInfo.presignedUrl) {
          continue;
        }

        uploadPromises.push(
          faqService.uploadToS3(presignedInfo.presignedUrl, image.file)
            .then(() => ({
              token: presignedInfo.token,
              fileSize: image.file.size,
              id: presignedInfo.id,
              success: true
            }))
            .catch(error => {
              return {
                token: presignedInfo.token,
                error: error.message,
                success: false
              };
            })
        );
      }

      const uploadResults = await Promise.all(uploadPromises);
      const successfulUploads = uploadResults.filter(result => result.success);

      if (successfulUploads.length === 0) {
        throw new Error('所有圖片上傳都失敗了');
      }

      const confirmPromises = successfulUploads.map(result =>
        faqService.confirmUpload(result.token, result.fileSize, isTest)
          .then(confirmResult => ({
            ...confirmResult,
            success: true
          }))
          .catch(error => {
            return {
              error: error.message,
              success: false
            };
          })
      );

      const confirmResults = await Promise.all(confirmPromises);
      const successfulConfirms = confirmResults.filter(result => result.success);

      const uploadedImageIds = successfulConfirms.map(result => {
        if (result && result.data) {
          if (result.data.data && result.data.data.id) {
            return result.data.data.id;
          }
          if (result.data.id) {
            return result.data.id;
          }
        }

        if (result && result.success && result.data && result.data.id) {
          return result.data.id;
        }

        if (result && result.id) {
          return result.id;
        }

        return null;
      }).filter(id => id !== null);

      return {
        uploadedImages: uploadedImageIds,
        totalAttempted: imagesToUpload.length,
        successfulUploads: successfulUploads.length,
        successfulConfirms: uploadedImageIds.length,
        errors: uploadResults.filter(result => !result.success).map(result => result.error)
      };
    } catch (error) {
      throw new Error(error.message || '上傳圖片時發生錯誤');
    }
  },

  /**
   * 新增 FAQ
   */
  createFaq: async (faqData, isTest = false) => {
    try {
      let processedData = { ...faqData };

      const images = processedData.images || [];
      delete processedData.images;
      // 標籤回答卡片由 FaqPage 單獨處理，不需要傳到 POST /faq
      delete processedData.tagAnswerCards;
      delete processedData.originalAnswerCards;

      if (processedData.keywords && Array.isArray(processedData.keywords)) {
        processedData.raw_keywords = JSON.stringify(processedData.keywords);
      } else if (processedData.raw_keywords && typeof processedData.raw_keywords !== 'string') {
        processedData.raw_keywords = JSON.stringify(processedData.raw_keywords);
      }

      const endpoint = isTest ? 'faq/test/create' : 'faq';
      const response = await apiRequest('post', endpoint, processedData, isTest);

      let createdFaq = null;
      if (response.data && response.data.data) {
        createdFaq = response.data.data.faq || response.data.data;
        if (createdFaq.generatedKeywords && !createdFaq.raw_keywords) {
          createdFaq.raw_keywords = JSON.stringify(createdFaq.generatedKeywords);
        }
      }

      if (images.length > 0 && createdFaq && createdFaq.id) {
        try {
          const imageUploadResult = await faqService.uploadFaqImages(images, createdFaq.id, isTest);

          if (response.data && response.data.data) {
            response.data.data.uploadedImageIds = imageUploadResult.uploadedImages;
            response.data.data.imageUploadSummary = {
              attempted: imageUploadResult.totalAttempted,
              successful: imageUploadResult.successfulConfirms,
              errors: imageUploadResult.errors || []
            };
          }
        } catch (imageError) {
          if (response.data && response.data.data) {
            response.data.data.imageUploadError = imageError.message;
          }
        }
      }

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '建立 FAQ 時發生錯誤');
    }
  },

  /**
   * 更新 FAQ
   */
  updateFaq: async (id, faqData, isTest = false) => {
    try {
      const faqId = parseInt(id);
      if (isNaN(faqId) || faqId <= 0) {
        throw new Error(`無效的 FAQ ID: ${id}`);
      }

      let processedData = { ...faqData };

      const images = processedData.images || [];
      const existingImageIds = processedData.existingImageIds || [];
      delete processedData.images;
      delete processedData.existingImageIds;
      delete processedData.imageIds;
      // 標籤回答卡片由 FaqPage 單獨處理，不需要傳到 PUT /faq/:id
      delete processedData.tagAnswerCards;
      delete processedData.originalAnswerCards;

      if (processedData.keywords !== undefined) {
        const keywordsArray = Array.isArray(processedData.keywords) ? processedData.keywords : [];
        processedData.raw_keywords = JSON.stringify(keywordsArray);
      } else if (processedData.raw_keywords) {
        if (typeof processedData.raw_keywords !== 'string') {
          processedData.raw_keywords = JSON.stringify(processedData.raw_keywords);
        }
      }

      let allImageIds = [...existingImageIds];

      if (images.length > 0) {
        try {
          const newImages = images.filter(img => img.file && !img.isExisting);

          if (newImages.length > 0) {
            const imageUploadResult = await faqService.uploadFaqImages(newImages, faqId, isTest);

            allImageIds = [...existingImageIds, ...imageUploadResult.uploadedImages];
          }
        } catch (imageError) {
          // 圖片上傳失敗，繼續更新 FAQ
        }
      }

      processedData.imageIds = allImageIds;

      const endpoint = isTest ? `faq/test/${faqId}` : `faq/${faqId}`;
      const response = await apiRequest('put', endpoint, processedData, isTest);

      return response.data;
    } catch (error) {
      if (error.message?.includes('無效的 FAQ ID')) {
        throw error;
      }

      throw new Error(error.response?.data?.message || '更新 FAQ 時發生錯誤');
    }
  },

  /**
   * 刪除 FAQ
   */
  deleteFaq: async (id, isTest = false) => {
    try {
      const endpoint = isTest ? `faq/test/${id}` : `faq/${id}`;
      const response = await apiRequest('delete', endpoint, null, isTest);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '刪除 FAQ 時發生錯誤');
    }
  },

  /**
   * 取得未回答 FAQ
   */
  getUnansweredFaqs: async (isTest = false, companyId = '2') => {
    try {
      let endpoint;
      if (isTest) {
        endpoint = `faq/test/unanswered?companyId=${companyId}`;
      } else {
        endpoint = 'faq/unanswered';
      }

      const response = await apiRequest('get', endpoint, null, isTest);
      return {
        data: response.data.items || []
      };
    } catch (error) {
      throw new Error(error.response?.data?.message || '取得未回答問題時發生錯誤');
    }
  },

  /**
   * 回答未回答 FAQ
   */
  answerFaq: async (id, answerData, isTest = false, companyId = '2') => {
    try {
      let endpoint;
      let data = { ...answerData };

      if (isTest) {
        endpoint = `faq/test/unanswered/${id}/answer`;
        data.companyId = companyId;
      } else {
        endpoint = `faq/unanswered/${id}/answer`;
      }

      const response = await apiRequest('put', endpoint, data, isTest);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '回答問題時發生錯誤');
    }
  },

  /**
   * 取得並格式化未回答 FAQ
   */
  fetchFormattedUnansweredFaqs: async (isTest = false) => {
    try {
      const response = await faqService.getUnansweredFaqs(isTest);

      const sortedQuestions = (response.data || []).sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);

        const isValidA = !isNaN(dateA.getTime());
        const isValidB = !isNaN(dateB.getTime());

        if (!isValidA && !isValidB) {
          return b.id - a.id;
        }

        if (!isValidA) return 1;
        if (!isValidB) return -1;

        return dateB - dateA;
      });

      const questionData = sortedQuestions.map((question, index) => ({
        ...question,
        id: question.id || `temp-id-${index}`,
        createdAt: question.created_at
      }));

      return questionData;
    } catch (error) {
      throw new Error(error.response?.data?.message || '無法載入未回答問題');
    }
  },

  /**
   * 取得 FAQ 範本 URL
   */
  getFaqTemplateUrl: () => {
    return '/templates/faq-template.xlsx';
  },

  /**
   * 取得指定 FAQ 的標籤回答列表
   * 回傳格式: { message, data: { faq, tagAnswers: [{ id, faqId, tagId, answer, priority, isActive, tag: { id, name, color } }] } }
   */
  getFaqTagAnswers: async (faqId) => {
    try {
      const response = await apiRequest('get', `faq/${faqId}/tag-answers`);
      return response.data; // { message, data: { faq, tagAnswers } }
    } catch (error) {
      throw new Error(error.response?.data?.message || '取得標籤回答時發生錯誤');
    }
  },

  /**
   * 批次取得多個 FAQ 的標籤答案摘要（單次請求取代 N 次）
   * GET /api/faq/batch-tag-answers?faqIds=1,2,3
   * 回傳格式: { message, data: { [faqId]: [{ id, faqId, tagId, priority, tag }] } }
   */
  getBatchTagAnswers: async (faqIds) => {
    try {
      if (!faqIds || faqIds.length === 0) return { data: {} };
      const response = await apiRequest('get', `faq/batch-tag-answers?faqIds=${faqIds.join(',')}`);
      return response.data; // { message, data: { [faqId]: [...] } }
    } catch (error) {
      throw new Error(error.response?.data?.message || '批次取得標籤回答時發生錯誤');
    }
  },

  /**
   * 建立或更新指定 FAQ + Tag 的標籤回答
   */
  createOrUpdateTagAnswer: async (faqId, tagId, data) => {
    try {
      const response = await apiRequest('post', `faq/${faqId}/tag-answers/${tagId}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '儲存標籤回答時發生錯誤');
    }
  },

  /**
   * 刪除指定 FAQ + Tag 的標籤回答
   */
  deleteTagAnswer: async (faqId, tagId) => {
    try {
      const response = await apiRequest('delete', `faq/${faqId}/tag-answers/${tagId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || '刪除標籤回答時發生錯誤');
    }
  },

  /**
   * 重新合成單一 FAQ 的語音檔（plan 068 Day 4）
   * 對應後端 POST /api/faq/:id/regenerate-audio
   * @returns {success, data:{faqId, audioStatus, estimatedSeconds, skipped?}}
   */
  regenerateAudio: async (faqId, { force = false } = {}) => {
    try {
      const response = await apiRequest('post', `faq/${faqId}/regenerate-audio`, { force });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || '重新合成語音時發生錯誤');
    }
  },

  /**
   * 批次重新合成 FAQ 語音（plan 068 Day 4）
   * 對應後端 POST /api/faq/regenerate-all-audio
   * @param {Object} opts
   * @param {'stale'|'failed'|'missing'|'all'} opts.filter
   * @param {string[]} opts.categories  optional category filter
   * @returns {success, data:{queuedCount, estimatedTotalSeconds}}
   */
  regenerateAllAudio: async ({ filter = 'stale', categories = [] } = {}) => {
    try {
      const response = await apiRequest('post', 'faq/regenerate-all-audio', {
        filter,
        categories
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || '批次重新合成語音時發生錯誤');
    }
  },

  /**
   * 取得目前公司各語音狀態的數量摘要（plan 068 Day 4）
   * 對應後端 GET /api/faq/audio-status
   * @returns {success, data:{companyId, byStatus:{ready,stale,pending,generating,failed,missing}}}
   */
  getAudioStatus: async () => {
    try {
      const response = await apiRequest('get', 'faq/audio-status');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || '取得語音狀態時發生錯誤');
    }
  }
};

export default faqService;
